// One editor for every event type — used for detailed logging and for
// editing/deleting anything on the timeline. Opened from anywhere via
// openEditor(); rendered once by <EventEditorHost/>.

import { Trash2 } from 'lucide-react';
import { useMemo, useState, useSyncExternalStore } from 'react';
import { SKILLS } from '../../content/training.ts';
import { SOCIAL_CATEGORIES } from '../../content/socialisation.ts';
import { deleteEvent, currentPerson, updateEvent } from '../../data/actions.ts';
import { useCollection, useEvents, useHousehold, useLiveStatus, useProfile } from '../../data/hooks.ts';
import { syncVisitTicks } from '../../data/visitors.ts';
import { VisitorAvatar } from '../visitors/Visitors.tsx';
import { sortForGrid, personStats } from '../../domain/visitors.ts';
import { uid } from '../../data/ids.ts';
import type { EventDataMap, EventType, GroomTask, LogEvent, Reaction, Symptom } from '../../domain/types.ts';
import { EVENT_META, symptomLabel } from '../../app/eventMeta.ts';
import { BCS_SCALE } from '../../domain/weight.ts';
import { Avatar, EndPicker, WhenPicker } from '../../ui/bits.tsx';
import { Button, Chips, Field, Segmented, Sheet, Tabs } from '../../ui/primitives.tsx';
import { toast } from '../../ui/toast.tsx';

// --- open/close store ------------------------------------------------------------

interface EditorState {
  event: LogEvent;
  isNew: boolean;
  title?: string;
}

let state: EditorState | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** Open the editor for an existing event. */
export function openEditor(event: LogEvent, title?: string) {
  state = { event, isNew: false, title };
  emit();
}

/** Open the editor to create a new event of a type, with optional defaults. */
export function openNew<T extends EventType>(type: T, data: EventDataMap[T], opts: { at?: number; end?: number; title?: string; note?: string } = {}) {
  const event = { id: uid(), type, at: opts.at ?? Date.now(), end: opts.end, by: currentPerson(), source: 'app', data, note: opts.note } as LogEvent;
  state = { event, isNew: true, title: opts.title };
  emit();
}

function close() {
  state = null;
  emit();
}

export function EventEditorHost() {
  const s = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
  );
  if (!s) return null;
  return <Editor key={s.event.id} initial={s.event} isNew={s.isNew} title={s.title} />;
}

// --- editor -------------------------------------------------------------------------

function Editor({ initial, isNew, title }: { initial: LogEvent; isNew: boolean; title?: string }) {
  const [ev, setEv] = useState<LogEvent>(initial);
  const household = useHousehold();
  const meta = EVENT_META[ev.type];
  const set = (patch: Partial<LogEvent>) => setEv((e) => ({ ...e, ...patch }) as LogEvent);
  const setData = (patch: Record<string, unknown>) => setEv((e) => ({ ...e, data: { ...e.data, ...patch } }) as LogEvent);
  const hasDuration = ev.type === 'sleep' || ev.type === 'walk' || ev.type === 'play' || ev.type === 'alone' || ev.type === 'visit';
  const people = useCollection('people');

  const valid = (() => {
    if (ev.type === 'weight') return ev.data.kg > 0 && ev.data.kg < 60;
    if (ev.type === 'meds') return ev.data.name.trim().length > 0;
    if (ev.type === 'social') return !!ev.data.itemId;
    if (ev.type === 'note') return !!ev.note?.trim();
    if (ev.type === 'visit') return ev.data.people.length > 0;
    if (ev.end !== undefined && ev.end < ev.at) return false;
    return true;
  })();

  const save = () => {
    updateEvent(ev);
    // A visit's socialisation ticks follow who came and how he found it.
    if (ev.type === 'visit') syncVisitTicks(ev, people);
    if (isNew) toast({ message: `${meta.label} logged`, icon: meta.icon, tone: 'good' });
    close();
    if (ev.type === 'health' && (ev.data.severity ?? 1) >= 3)
      toast({ message: 'That sounds serious', detail: 'If you are worried, call the vet now — see Emergency.', tone: 'bad', duration: 10_000 });
  };

  return (
    <Sheet
      open
      onClose={close}
      title={title ?? (isNew ? `Log ${meta.label.toLowerCase()}` : `Edit ${meta.label.toLowerCase()}`)}
      wide={ev.type === 'social' || ev.type === 'training' || ev.type === 'visit'}
      footer={
        <>
          {!isNew && (
            <Button
              variant="ghost"
              icon={Trash2}
              onClick={() => {
                deleteEvent(ev.id);
                if (ev.type === 'visit') syncVisitTicks(null, people, ev.id);
                toast({
                  message: `${meta.label} deleted`,
                  actions: [{ label: 'Undo', onClick: () => (updateEvent(initial), initial.type === 'visit' && syncVisitTicks(initial, people)) }],
                });
                close();
              }}
            >
              Delete
            </Button>
          )}
          <span className="spacer" />
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!valid} onClick={save}>
            {isNew ? 'Save' : 'Save changes'}
          </Button>
        </>
      }
    >
      <div className="form">
        <TypeFields ev={ev} setData={setData} set={set} />

        <WhenPicker label={ev.type === 'sleep' ? 'Went down' : ev.type === 'visit' ? 'Arrived' : hasDuration ? 'Started' : 'When'} value={ev.at} onChange={(at) => set({ at })} quick={isNew} />
        {hasDuration && (
          <EndPicker
            start={ev.at}
            value={ev.end}
            onChange={(end) => set({ end })}
            label={ev.type === 'sleep' ? 'Woke up' : ev.type === 'walk' ? 'Back home' : ev.type === 'visit' ? 'Left' : 'Finished'}
            ongoing={ev.type === 'sleep' ? 'Still asleep' : ev.type === 'visit' ? 'Still here' : 'Still going'}
          />
        )}

        <Field label={ev.type === 'visit' ? 'Logged by' : 'Who'}>
          <div className="chips">
            {household.people.map((p) => (
              <button key={p.id} type="button" className={`chip chip-person${ev.by === p.id ? ' on' : ''}`} onClick={() => set({ by: ev.by === p.id ? undefined : p.id })}>
                <Avatar person={p} size={22} /> {p.name}
              </button>
            ))}
          </div>
        </Field>

        {ev.type !== 'note' && (
          <Field label="Note (optional)">
            <textarea className="input" rows={2} value={ev.note ?? ''} onChange={(e) => set({ note: e.target.value || undefined })} />
          </Field>
        )}
      </div>
    </Sheet>
  );
}

function TypeFields({ ev, setData, set }: { ev: LogEvent; setData: (p: Record<string, unknown>) => void; set: (p: Partial<LogEvent>) => void }) {
  const profile = useProfile();
  const status = useLiveStatus();
  switch (ev.type) {
    case 'wee':
    case 'poo':
      return (
        <>
          <Field label="What">
            <Segmented
              value={ev.type}
              onChange={(type) => set({ type, data: type === 'poo' ? { where: ev.data.where } : { where: ev.data.where } } as Partial<LogEvent>)}
              options={[
                { value: 'wee', label: 'Wee' },
                { value: 'poo', label: 'Poo' },
              ]}
            />
          </Field>
          <Field label="Where">
            <Segmented
              value={ev.data.where}
              onChange={(where) => setData({ where })}
              options={[
                { value: 'outside', label: 'Outside' },
                { value: 'inside', label: 'Indoors (accident)' },
              ]}
            />
          </Field>
          {ev.type === 'poo' && (
            <Field label="Consistency" hint="Firm and log-shaped is ideal. Runny for more than a day, or any blood, is worth a call to the vet.">
              <Segmented
                value={ev.data.quality}
                onChange={(quality) => setData({ quality })}
                options={[
                  { value: 'firm', label: 'Firm' },
                  { value: 'soft', label: 'Soft' },
                  { value: 'runny', label: 'Runny' },
                  { value: 'hard', label: 'Hard' },
                ]}
              />
            </Field>
          )}
        </>
      );
    case 'meal':
      return (
        <>
          <Field label="Amount (g)" hint={status.meals.gramsPerMeal ? `Plan: ${status.meals.gramsPerMeal} g per meal (${profile.food.dailyGrams} g a day ÷ ${status.meals.perDay})` : 'Set his daily amount on the Food page.'}>
            <Stepper value={ev.data.grams ?? status.meals.gramsPerMeal ?? 0} step={5} onChange={(grams) => setData({ grams })} unit="g" />
          </Field>
          <Field label="How did he eat?">
            <Segmented
              value={ev.data.appetite}
              onChange={(appetite) => setData({ appetite })}
              options={[
                { value: 'all', label: 'All of it' },
                { value: 'most', label: 'Most' },
                { value: 'some', label: 'Some' },
                { value: 'none', label: 'None' },
              ]}
            />
          </Field>
        </>
      );
    case 'sleep':
      return (
        <Field label="Where">
          <Segmented
            value={ev.data.place}
            onChange={(place) => setData({ place })}
            options={[
              { value: 'crate', label: 'Crate' },
              { value: 'bed', label: 'Bed' },
              { value: 'sofa', label: 'Sofa' },
              { value: 'lap', label: 'Lap' },
              { value: 'car', label: 'Car' },
            ]}
          />
        </Field>
      );
    case 'walk':
      return (
        <Field label="Type" hint={status.weeks < 12 ? 'Before his vaccinations are complete, carry him — he can still see, hear and smell the world.' : undefined}>
          <Segmented
            value={ev.data.carried ? 'carried' : ev.data.run ? 'run' : 'walk'}
            onChange={(v) => setData({ carried: v === 'carried', run: v === 'run' })}
            options={[
              { value: 'walk', label: 'On foot' },
              { value: 'carried', label: 'Carried / sling' },
              ...(status.age && status.age.months >= 12 ? [{ value: 'run', label: 'Run together' }] : []),
            ]}
          />
        </Field>
      );
    case 'alone':
      return (
        <Field label="How did he do?" hint="Build up from seconds. If he cried or panicked, go back a step next time.">
          <Segmented
            value={ev.data.settled === undefined ? undefined : ev.data.settled ? 'yes' : 'no'}
            onChange={(v) => setData({ settled: v === 'yes' })}
            options={[
              { value: 'yes', label: 'Settled' },
              { value: 'no', label: 'Unsettled' },
            ]}
          />
        </Field>
      );
    case 'training':
      return <TrainingFields ev={ev} setData={setData} weeks={status.weeks} />;
    case 'social':
      return <SocialFields ev={ev} setData={setData} />;
    case 'visit':
      return <VisitFields ev={ev} setData={setData} />;
    case 'weight':
      return (
        <>
        <Field label="Weight (kg)" hint="Kitchen or baby scales while he’s small. Later: weigh yourself holding him, then subtract.">
          <Stepper value={ev.data.kg} step={0.05} decimals={2} onChange={(kg) => setData({ kg })} unit="kg" />
        </Field>
        <Field label="Body condition (optional)" hint={BCS_SCALE.find((b) => b.score === ev.data.bcs)?.detail ?? 'Feel his ribs and look from above: 4–5 is ideal — ribs easy to feel, visible waist.'}>
          <div className="bcs-picker">
            {BCS_SCALE.map((b) => (
              <button key={b.score} type="button" className={`bcs${ev.data.bcs === b.score ? ' on' : ''}${b.score === 4 || b.score === 5 ? ' ideal' : ''}`} onClick={() => setData({ bcs: ev.data.bcs === b.score ? undefined : b.score })} title={b.label}>
                {b.score}
              </button>
            ))}
          </div>
        </Field>
        </>
      );
    case 'groom':
      return (
        <Field label="What did you do?">
          <Chips<GroomTask>
            value={ev.data.tasks}
            onChange={(tasks) => setData({ tasks })}
            options={[
              { value: 'brush', label: 'Brush' },
              { value: 'ears', label: 'Ears checked' },
              { value: 'teeth', label: 'Teeth' },
              { value: 'nails', label: 'Nails' },
              { value: 'eyes', label: 'Eyes' },
              { value: 'bath', label: 'Bath' },
              { value: 'feathers', label: 'Feather trim' },
              { value: 'pro', label: 'Groomer' },
            ]}
          />
        </Field>
      );
    case 'meds':
      return (
        <>
          <Field label="Medicine / product">
            <input className="input" value={ev.data.name} onChange={(e) => setData({ name: e.target.value })} placeholder="e.g. worming tablet" />
          </Field>
          <Field label="Dose (optional)">
            <input className="input" value={ev.data.dose ?? ''} onChange={(e) => setData({ dose: e.target.value || undefined })} />
          </Field>
        </>
      );
    case 'health':
      return (
        <>
          <Field label="What’s up?">
            <div className="chips">
              {(['vomit', 'diarrhoea', 'not-eating', 'lethargic', 'itching', 'ear-shaking', 'limping', 'coughing', 'eye', 'ate-something', 'other'] as Symptom[]).map((s) => (
                <button key={s} type="button" className={`chip${ev.data.symptom === s ? ' on' : ''}`} onClick={() => setData({ symptom: s })}>
                  {symptomLabel(s)}
                </button>
              ))}
            </div>
          </Field>
          <Field label="How bad?" hint="Anything severe, or if you are worried at all: call the vet. Unvaccinated puppy + vomiting/diarrhoea = call today.">
            <Segmented
              value={ev.data.severity}
              onChange={(severity) => setData({ severity })}
              options={[
                { value: 1, label: 'Mild' },
                { value: 2, label: 'Moderate' },
                { value: 3, label: 'Severe' },
              ]}
            />
          </Field>
        </>
      );
    case 'treat':
      return (
        <Field label="What" hint="Treats and chews should stay under ~10% of his daily food.">
          <input className="input" value={ev.data.what ?? ''} onChange={(e) => setData({ what: e.target.value || undefined })} placeholder="e.g. Kong, lick mat, chew" />
        </Field>
      );
    case 'note':
      return (
        <Field label="Note">
          <textarea className="input" rows={4} value={ev.note ?? ''} onChange={(e) => set({ note: e.target.value || undefined })} autoFocus />
        </Field>
      );
    default:
      return null;
  }
}

function TrainingFields({ ev, setData, weeks }: { ev: LogEvent<'training'>; setData: (p: Record<string, unknown>) => void; weeks: number }) {
  const [showAll, setShowAll] = useState(false);
  const skills = useMemo(() => SKILLS.filter((s) => showAll || s.fromWeeks <= weeks + 4 || ev.data.skills.includes(s.id)), [showAll, weeks, ev.data.skills]);
  return (
    <>
      <Field label="What did you practise?" hint="Little and often: 2–5 minutes, a few times a day, ending on a win.">
        <Chips value={ev.data.skills} onChange={(s) => setData({ skills: s })} options={skills.map((s) => ({ value: s.id, label: s.name }))} />
      </Field>
      {!showAll && (
        <button type="button" className="link" onClick={() => setShowAll(true)}>
          Show all skills
        </button>
      )}
      <Field label="How did it go?">
        <Segmented
          value={ev.data.rating}
          onChange={(rating) => setData({ rating })}
          options={[
            { value: 1, label: 'Tricky' },
            { value: 2, label: 'Okay' },
            { value: 3, label: 'Nailed it' },
          ]}
        />
      </Field>
    </>
  );
}

function SocialFields({ ev, setData }: { ev: LogEvent<'social'>; setData: (p: Record<string, unknown>) => void }) {
  const initialCat = SOCIAL_CATEGORIES.find((c) => c.items.some((i) => i.id === ev.data.itemId))?.id ?? SOCIAL_CATEGORIES[0]?.id;
  const [cat, setCat] = useState(initialCat);
  const category = SOCIAL_CATEGORIES.find((c) => c.id === cat);
  return (
    <>
      <Tabs value={cat} onChange={setCat} tabs={SOCIAL_CATEGORIES.map((c) => ({ value: c.id, label: c.title }))} />
      <div className="chips chips-wrap">
        {category?.items.map((i) => (
          <button key={i.id} type="button" className={`chip${ev.data.itemId === i.id ? ' on' : ''}`} onClick={() => setData({ itemId: i.id })}>
            {i.label}
          </button>
        ))}
      </div>
      <Field label="How did he react?">
        <Segmented
          value={ev.data.reaction}
          onChange={(reaction) => setData({ reaction })}
          size="lg"
          options={[
            { value: 'happy', label: 'Happy / curious' },
            { value: 'unsure', label: 'Unsure' },
            { value: 'worried', label: 'Worried' },
          ]}
        />
      </Field>
    </>
  );
}

export function Stepper({ value, onChange, step = 1, decimals = 0, unit, showZero }: { value: number; onChange: (v: number) => void; step?: number; decimals?: number; unit?: string; showZero?: boolean }) {
  const round = (v: number) => Number(Math.max(0, v).toFixed(decimals));
  return (
    <div className="stepper">
      <button type="button" onClick={() => onChange(round(value - step))} aria-label="Less">
        −
      </button>
      <input
        className="input"
        inputMode="decimal"
        value={value || showZero ? String(value) : ''}
        onChange={(e) => {
          const v = parseFloat(e.target.value.replace(',', '.'));
          onChange(Number.isNaN(v) ? 0 : v);
        }}
      />
      {unit && <span className="stepper-unit">{unit}</span>}
      <button type="button" onClick={() => onChange(round(value + step))} aria-label="More">
        +
      </button>
    </div>
  );
}

const REACTION_OPTIONS: { value: Reaction | 'none'; label: string }[] = [
  { value: 'happy', label: 'Happy' },
  { value: 'unsure', label: 'Unsure' },
  { value: 'worried', label: 'Worried' },
  { value: 'none', label: 'Not sure' },
];

/** Who came (faces from Arlo's people) and how he found it. */
function VisitFields({ ev, setData }: { ev: LogEvent<'visit'>; setData: (p: Record<string, unknown>) => void }) {
  const people = useCollection('people');
  const events = useEvents();
  const grid = useMemo(() => sortForGrid(people, personStats(events)), [people, events]);
  const toggle = (id: string) => {
    const on = ev.data.people.includes(id);
    const ids = on ? ev.data.people.filter((x) => x !== id) : [...ev.data.people, id];
    // Names kept with the visit, so it still reads right if someone's later removed.
    const names = ids.map((x) => people.find((p) => p.id === x)?.name ?? ev.data.names[ev.data.people.indexOf(x)] ?? '');
    setData({ people: ids, names });
  };
  return (
    <>
      <Field label="Who came?">
        {grid.length ? (
          <div className="visitor-grid visitor-grid-compact">
            {grid.map((p) => {
              const on = ev.data.people.includes(p.id);
              return (
                <button key={p.id} type="button" className={`visitor-tile${on ? ' on' : ''}`} aria-pressed={on} onClick={() => toggle(p.id)}>
                  <span className="visitor-tile-face">
                    <VisitorAvatar p={p} size={52} />
                  </span>
                  <span className="visitor-name">{p.name.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="muted small">Add people first: Today → Visitor, or Socialise → Arlo’s people.</p>
        )}
      </Field>
      <Field label="How did he find it?" hint="Their traits tick off his socialisation checklist.">
        <Segmented value={ev.data.reaction ?? 'none'} onChange={(r) => setData({ reaction: r === 'none' ? undefined : r })} options={REACTION_OPTIONS} />
      </Field>
    </>
  );
}
